/**
 * UTC date helpers for usage windows.
 *
 * The proxy buckets `LiteLLM_DailyTagSpend` by **UTC** date. Building a window from local
 * dates therefore drifts by a day for anyone west of Greenwich, silently shifting or
 * dropping a day's spend.
 *
 * `AiUsageService.ts` has exactly that bug today: `start.setDate(start.getDate() - 30)`
 * mutates in local time and is then formatted with `toISOString()`, which is UTC — so the
 * window it asks for is not the window it computed. These helpers stay in UTC end to end.
 */

/** `YYYY-MM-DD` in UTC. */
export type DateOnly = string;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

export function isDateOnly(value: string): boolean {
  return DATE_ONLY.test(value);
}

export function toDateOnly(date: Date): DateOnly {
  return date.toISOString().slice(0, 10);
}

/** Midnight UTC on a `YYYY-MM-DD`, so arithmetic never touches local time. */
function parseUtc(date: DateOnly): number {
  return Date.parse(`${date}T00:00:00.000Z`);
}

export function addDays(date: DateOnly, days: number): DateOnly {
  return toDateOnly(new Date(parseUtc(date) + days * MS_PER_DAY));
}

export function daysBetween(start: DateOnly, end: DateOnly): number {
  return Math.round((parseUtc(end) - parseUtc(start)) / MS_PER_DAY) + 1;
}

/** Today in UTC. */
export function todayUtc(now: Date = new Date()): DateOnly {
  return toDateOnly(now);
}

/**
 * The default window: the `days` complete days ending **yesterday**.
 *
 * Ending yesterday rather than today is deliberate. Today's bucket is still being written,
 * and the tag and team aggregates are separate tables flushed by the same background writer
 * — so a window including today can show a numerator ahead of its denominator, i.e. coverage
 * above 100%, for reasons that have nothing to do with attribution. The UI renders today
 * separately and labels it partial.
 */
export function defaultWindow(
  days: number,
  now: Date = new Date(),
): { startDate: DateOnly; endDate: DateOnly } {
  const endDate = addDays(todayUtc(now), -1);
  return { startDate: addDays(endDate, -(days - 1)), endDate };
}
